import "./gtakNew.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "react-quill-new/dist/quill.snow.css";
import {
  TextField,
  Select,
  MenuItem,
  Button,
  FormHelperText,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Typography,
  CircularProgress,
  Backdrop,
  LinearProgress,
  Box,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";

const GtakNew = ({ title }) => {
  const userEmail = useSelector((state) => state.user.userEmail);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    emailRef: userEmail,
    videoTitle: "",
    videoCategory: "All",
    videoDescription: "",
  });

  const [videoFile, setVideoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 🆕 Track progress
  const [errors, setErrors] = useState({});
  const [responseMessage, setResponseMessage] = useState({
    text: "",
    color: "",
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleVideoChange = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const MAX_SIZE = 50 * 1024 * 1024;

    const allowedTypes = ["video/mp4", "video/webm", "video/quicktime"];

    if (!allowedTypes.includes(file.type)) {
      setErrors({ videoFile: "Only MP4, MOV, WEBM allowed" });
      return;
    }

    if (file.size > MAX_SIZE) {
      setErrors({ videoFile: "File must be less than 50MB" });
      return;
    }

    setVideoFile(file);
    setErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.emailRef) errors.emailRef = "Ref. Email is required.";
    if (!formData.videoTitle) errors.videoTitle = "Video Title is required.";
    if (!formData.videoCategory)
      errors.videoCategory = "Video Category is required.";
    if (!videoFile) errors.videoFile = "Video File is required.";

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (videoFile.size > 50 * 1024 * 1024) {
      setResponseMessage({
        text: "File too large (Max 50MB)",
        color: "red",
      });
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    setResponseMessage({ text: "", color: "" });

    try {
      // 1️⃣ Get Signed Signature from Backend
      const { data: signData } = await axios.get(
        `${BASE_URL}/api/userVideoFile/generate-signature`,
      );

      console.log("Lokendra Signdata:", signData);
      // 2️⃣ Prepare Direct Upload to Cloudinary
      const cloudData = new FormData();
      cloudData.append("file", videoFile);
      cloudData.append("api_key", signData.api_key);
      cloudData.append("timestamp", signData.timestamp);
      cloudData.append("signature", signData.signature);
      cloudData.append("folder", signData.folder);
      // cloudData.append("eager", "sp_auto:m3u8");
      // cloudData.append("resource_type", "video");

      const cloudinaryUrl = `https://api.cloudinary.com/v1_1/${signData.cloud_name}/video/upload`;

      const cloudRes = await axios.post(cloudinaryUrl, cloudData, {
        timeout: 600000,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setUploadProgress(percent);
          }
        },
      });

      // 3️⃣ Save link and metadata to MongoDB
      const response = await axios.post(
        `${BASE_URL}/api/userVideoFile/save-metadata`,
        {
          emailRef: formData.emailRef,
          videoTitle: formData.videoTitle,
          videoCategory: formData.videoCategory,
          videoDescription: formData.videoDescription,
          public_id: cloudRes.data.public_id,
          secure_url: cloudRes.data.secure_url,
        },
      );

      if (response.data.success) {
        setResponseMessage({
          text: "Reel published successfully!",
          color: "green",
        });
        setTimeout(() => navigate("/gtak"), 1500);
      }
    } catch (error) {
      console.error(error);
      console.log("FULL ERROR:", error.response?.data || error);
      setResponseMessage({
        text:
          error.response?.data?.message || "Upload failed. Try a smaller file.",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="newNews">
      <Sidebar />
      <div className="newNewsContainer">
        <Navbar />
        <div className="top">
          <h1>{title}</h1>
        </div>
        <div className="bottom">
          <form
            onSubmit={handleSubmit}
            style={{ display: "grid", gap: "1rem", width: "100%" }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <input
                  accept="video/*"
                  id="videoFile"
                  type="file"
                  hidden
                  onChange={handleVideoChange}
                  disabled={loading}
                />
                <label htmlFor="videoFile">
                  <Box
                    sx={{
                      border: "2px dashed #ccc",
                      p: 3,
                      textAlign: "center",
                      cursor: "pointer",
                    }}
                  >
                    <IconButton
                      color="primary"
                      component="span"
                      disabled={loading}
                    >
                      <CloudUploadIcon sx={{ fontSize: 40 }} />
                    </IconButton>
                    <Typography variant="body1">
                      {videoFile
                        ? `Selected: ${videoFile.name}`
                        : "Click to select Gaunle Reel (Video)"}
                    </Typography>
                  </Box>
                </label>
                {errors.videoFile && (
                  <Typography color="error">{errors.videoFile}</Typography>
                )}
              </Grid>
            </Grid>

            {loading && (
              <Box sx={{ width: "100%", mt: 2 }}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                >
                  Uploading to Cloud: {uploadProgress}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
            )}

            <TextField
              fullWidth
              label="Video Title"
              name="videoTitle"
              value={formData.videoTitle}
              onChange={handleInputChange}
              error={Boolean(errors.videoTitle)}
              helperText={errors.videoTitle}
              required
              disabled={loading}
            />

            <FormControl
              fullWidth
              margin="normal"
              error={Boolean(errors.videoCategory)}
              disabled={loading}
            >
              <InputLabel>Category</InputLabel>
              <Select
                name="videoCategory"
                value={formData.videoCategory}
                onChange={handleInputChange}
                label="Category"
              >
                <MenuItem value="Dance">Dance</MenuItem>
                <MenuItem value="Comedy">Comedy</MenuItem>
                <MenuItem value="Documentary">Documentary</MenuItem>
                <MenuItem value="Competition">Competition</MenuItem>
                <MenuItem value="Misc">Misc</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Description"
              name="videoDescription"
              value={formData.videoDescription}
              onChange={handleInputChange}
              multiline
              rows={3}
              disabled={loading}
            />

            {responseMessage.text && (
              <Typography
                style={{
                  color: responseMessage.color,
                  textAlign: "center",
                  marginTop: "1rem",
                }}
              >
                {responseMessage.text}
              </Typography>
            )}

            <div
              style={{
                display: "flex",
                justifySelf: "center",
                marginTop: "1rem",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading ? "Publishing..." : "Publish Video"}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <Box textAlign="center">
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Uploading Reel... {uploadProgress}%
          </Typography>
        </Box>
      </Backdrop>
    </div>
  );
};

export default GtakNew;
